import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/* =============================================================================
   Membership plans for an organization landing page.

   Shared by the generic landing (PublicLanding) and every custom organization
   landing under src/orgs/. The pricing is the PLATFORM's, not the partner's —
   an organization chooses which of our tiers it offers, and nothing else. Kept
   in one component so a custom landing can never quote a stale price.

   Renders nothing when the org lists no plans, so "/" is unaffected.
   ========================================================================== */

/** Shape returned by /lookups/tiers (only the fields the plan cards need). */
export interface LiveTier {
  id: number;
  name: string;
  amount_per_month: number;
  amount_per_year: number | null;
  features_text?: string | null;
}

/** The free plan every buyer sits on until they subscribe. Kept in step with
 *  BASIC_FEATURES in ChoosePlan.tsx — same tier, same promise. */
const BASIC_FEATURES = [
  "Browse all Active and Coming Soon listings",
  "View listing category",
  "Deal financials — asking price, revenue, EBITDA & multiples",
  "Basic filters",
];

/** Tier features are stored as free text, one per line. */
function tierFeatures(tier: LiveTier): string[] {
  return (tier.features_text ?? "")
    .split("\n")
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

export default function OrgPlans({
  slug,
  tierNames,
  heading = "Choose the access level that matches your search.",
  eyebrow = "Membership",
  className,
}: {
  /** Organization slug — the register CTA must point at THAT org's door, or the
   *  new account joins BusinessLocating instead. */
  slug: string;
  /** Plan names the org offers, in display order. */
  tierNames: string[];
  heading?: string;
  eyebrow?: string;
  className?: string;
}) {
  const [tiers, setTiers] = useState<LiveTier[]>([]);
  const registerTo = `/${slug}/register`;

  useEffect(() => {
    if (tierNames.length === 0) return;
    let cancelled = false;
    api
      .get<LiveTier[]>("/lookups/tiers")
      .then((data) => {
        if (!cancelled) setTiers(data);
      })
      .catch(() => {
        /* Plans section is simply omitted — every other CTA still works. */
      });
    return () => {
      cancelled = true;
    };
  }, [tierNames.length]);

  // Resolved by NAME, in the order the org listed them. Tier ids are SERIAL and
  // differ per environment, so an id stored on the org would point at a
  // different plan in prod than in dev.
  const orgTiers = tierNames
    .map((name) => tiers.find((t) => t.name.toLowerCase() === name.toLowerCase()))
    .filter((t): t is LiveTier => Boolean(t));

  if (orgTiers.length === 0) return null;

  return (
    <section
      id="plans"
      className={cn(
        "scroll-mt-20 border-b border-border/60 bg-card/40 px-4 py-16 sm:px-6 lg:px-8",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="text-center">
          <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
            {eyebrow}
          </div>
          <h2 className="mx-auto mt-2.5 max-w-2xl font-display text-3xl text-foreground sm:text-4xl">
            {heading}
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Basic: free, no tier, no checkout. */}
          <div className="relative flex h-full flex-col rounded-2xl border border-border/60 bg-card/70 p-6 sm:p-7">
            <p className="font-display text-2xl text-foreground">Basic</p>
            <div className="mt-4 flex items-end gap-1.5">
              <span className="text-[2.75rem] font-extrabold leading-none tracking-tight text-foreground tabular-nums">
                $0
              </span>
              <span className="pb-1.5 text-sm font-medium text-muted-foreground">forever</span>
            </div>
            <p className="mt-2.5 text-[13px] leading-5 text-muted-foreground">
              no card required · <span className="text-primary">upgrade any time</span>
            </p>

            <Link to={registerTo} className="mt-6">
              <Button size="lg" variant="outline" className="w-full border-primary/40">
                Start free
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>

            <p className="mt-5 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-primary">
              Includes
            </p>
            <ul className="mt-4 space-y-3 border-t border-border/60 pt-5">
              {BASIC_FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-2.5 text-[13px] leading-6 text-foreground/80"
                >
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {orgTiers.map((tier, index) => {
            // The last plan an org lists is its top plan — highlight it.
            const highlight = index === orgTiers.length - 1 && orgTiers.length > 1;
            const features = tierFeatures(tier);
            return (
              <div
                key={tier.id}
                className={cn(
                  "relative flex h-full flex-col rounded-2xl border p-6 sm:p-7",
                  highlight
                    ? "border-primary/60 bg-gradient-to-b from-primary/[0.08] to-transparent shadow-[0_0_60px_-15px_rgba(212,169,62,0.4)]"
                    : "border-border/60 bg-card/70",
                )}
              >
                {highlight && (
                  <span className="absolute -top-3 left-7 inline-flex items-center whitespace-nowrap rounded-full bg-gold-gradient px-3.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-primary-foreground shadow-lg">
                    Most access
                  </span>
                )}

                <p className={cn("font-display text-2xl", highlight ? "text-primary" : "text-foreground")}>
                  {tier.name}
                </p>

                <div className="mt-4 flex items-end gap-1.5">
                  <span className="text-[2.75rem] font-extrabold leading-none tracking-tight text-foreground tabular-nums">
                    ${tier.amount_per_month.toLocaleString()}
                  </span>
                  <span className="pb-1.5 text-sm font-medium text-muted-foreground">per month</span>
                </div>

                {tier.amount_per_year != null && (
                  <p className="mt-2.5 text-[13px] leading-5 text-muted-foreground">
                    or{" "}
                    <span className="font-semibold text-foreground/80">
                      ${tier.amount_per_year.toLocaleString()}
                    </span>{" "}
                    annually
                  </p>
                )}

                <Link to={registerTo} className="mt-6">
                  <Button
                    size="lg"
                    variant={highlight ? "default" : "outline"}
                    className={cn("w-full", highlight ? "shadow-glow" : "border-primary/40")}
                  >
                    Select {tier.name}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>

                {features.length > 0 && (
                  <>
                    <p className="mt-5 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-primary">
                      Includes
                    </p>
                    <ul className="mt-4 space-y-3 border-t border-border/60 pt-5">
                      {features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2.5 text-[13px] leading-6 text-foreground/80"
                        >
                          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
